import { useCallback, useEffect, useRef, useState } from 'react';

type Props<T> = {
  enabled: boolean;
  interval: number;
  queryKey: string;
  fetcher: ({ signal }: { signal?: AbortSignal }) => Promise<T>;
  getCompleteCondition: (data: T) => boolean;
  onComplete: (data: T) => void;
  onFetched?: (data: T) => void;
  onError?: (error: unknown) => void;
};

/**
 * @description A custom hook that periodically calls an API until a specific condition is met.
 * It uses the 'Ref Pattern' to allow users to conveniently use function props without wrapping them in `useCallback`.
 * @param {Props<T>} props - The props object for configuring the polling.
 * @prop {boolean} enabled - Determines whether polling is active.
 * @prop {number} interval - The polling interval in milliseconds.
 * @prop {() => Promise<T>} fetcher - The async function that fetches data.
 * @prop {(data: T) => boolean} getCondition - A function that determines the condition to stop polling. Polling stops if it returns true.
 * @prop {(data: T) => void} onDone - The callback function that is called when the polling condition is met.
 * @prop {(data: T) => void} onFetched - The callback function that is called on every successful data fetch.
 * @returns {number} The current polling count.
 */
export const usePolling = <T extends object>(props: Props<T>) => {
  const { enabled, interval, queryKey } = props;

  // --- Ref Pattern Start ---
  // 1. Store the props in a ref object.
  // This ref refers to the same object throughout the hook's lifecycle, allowing its inner value to be changed without causing a re-render.
  const savedProps = useRef(props);

  // 2. Store the abort controller in a ref object.
  const abortControllerRef = useRef<AbortController | null>(null);

  // 2. Update the ref with the latest props on every re-render of the parent component.
  // This ensures that the `fetchData` function always references the latest `fetcher`, `onDone`, etc.
  useEffect(() => {
    savedProps.current = props;
  }, [props]);
  // --- Ref Pattern End ---

  const initialized = useRef(false);
  const [pollingCount, setPollingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // 3. Wrap `fetchData` with `useCallback` but with an empty dependency array.
  // This function is now created only once when the component mounts and is not recreated.
  // Inside the function, we can always access the latest props via `savedProps.current`.
  const fetchData = useCallback(async () => {
    const {
      fetcher,
      getCompleteCondition: getCondition,
      onComplete: onDone,
      onFetched,
    } = savedProps.current;

    const abortController = new AbortController();
    abortControllerRef.current = abortController; // 현재
    try {
      setLoading(true);
      const result = await fetcher({ signal: abortController.signal });

      if (!abortController.signal.aborted) {
        setPollingCount((prev) => prev + 1);
        onFetched?.(result);
        if (getCondition(result)) {
          onDone(result);
          setLoading(false);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
        }
      }
    } catch (error) {
      // AbortError는 정상적인 취소이므로 무시
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Error in usePolling:', error);
      savedProps.current.onError?.(error);
      setLoading(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (!initialized.current) {
      initialized.current = true;
      void fetchData();
    }

    // 4. `setInterval` now uses the stable `fetchData` reference.
    // Therefore, this `useEffect` only re-runs if the `enabled` or `interval` values change.
    intervalRef.current = setInterval(fetchData, interval);

    return () => {
      initialized.current = false;
      setPollingCount(0);

      // 진행 중인 요청 취소
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, interval, fetchData, queryKey]);

  return {
    pollingCount,
    loading,
  };
};
